import React, { useState, useEffect, useRef, useCallback } from "react";
import api from "../api/axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import BottomTabNav from "../components/BottomTabNav";
import BetaAdCard from "../components/BetaAdCard";
import useAdManager from "../hooks/useAdManager";
import useUserStatus from "../hooks/useUserStatus";
import { useAudioManager, useMicrophoneManager } from "../hooks/useAudioManager";
import { Mic, Volume2, Heart, ArrowRight, Copy, Check, MessageCircle, ExternalLink, Loader2, ArrowLeftRight, AlertCircle, TestTube2, MicOff, VolumeX, Settings2, X, ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

// ============================================================
// DIALECT OPTIONS BY LANGUAGE
// ============================================================

// Spanish dialect options - use i18n keys
const getSpanishDialectOptions = (t) => [
  { code: "NEUTRAL", label: t('dialects.spanish.neutral', 'Neutral Spanish'), flag: "🌎" },
  { code: "DO", label: t('dialects.spanish.dominican', 'Dominican'), flag: "🇩🇴" },
  { code: "MX", label: t('dialects.spanish.mexican', 'Mexican'), flag: "🇲🇽" },
  { code: "ES", label: t('dialects.spanish.spain', 'Spain'), flag: "🇪🇸" },
  { code: "CO", label: t('dialects.spanish.colombian', 'Colombian'), flag: "🇨🇴" },
  { code: "AR", label: t('dialects.spanish.argentine', 'Argentine'), flag: "🇦🇷" }
];

// French dialect options - use i18n keys
const getFrenchDialectOptions = (t) => [
  { code: "NEUTRAL", label: t('dialects.french.neutral', 'Neutral French'), flag: "🌎" },
  { code: "FR", label: t('dialects.french.france', 'France'), flag: "🇫🇷" },
  { code: "CA", label: t('dialects.french.canadian', 'Canadian French'), flag: "🇨🇦" },
  { code: "BE", label: t('dialects.french.belgian', 'Belgian French'), flag: "🇧🇪" },
  { code: "CH", label: t('dialects.french.swiss', 'Swiss French'), flag: "🇨🇭" },
  { code: "SN", label: t('dialects.french.african', 'West African French'), flag: "🇸🇳" }
];

// Arabic dialect options - use i18n keys
const getArabicDialectOptions = (t) => [
  { code: "NEUTRAL", label: t('dialects.arabic.neutral', 'Modern Standard Arabic'), flag: "🌎" },
  { code: "EG", label: t('dialects.arabic.egyptian', 'Egyptian Arabic'), flag: "🇪🇬" },
  { code: "SA", label: t('dialects.arabic.gulf', 'Gulf Arabic'), flag: "🇸🇦" },
  { code: "MA", label: t('dialects.arabic.moroccan', 'Moroccan Arabic'), flag: "🇲🇦" },
  { code: "LB", label: t('dialects.arabic.levantine', 'Levantine Arabic'), flag: "🇱🇧" },
  { code: "IQ", label: t('dialects.arabic.iraqi', 'Iraqi Arabic'), flag: "🇮🇶" }
];

// Get dialect options based on target language
const getDialectOptions = (targetLang, t) => {
  switch (targetLang) {
    case 'es': return getSpanishDialectOptions(t);
    case 'fr': return getFrenchDialectOptions(t);
    case 'ar': return getArabicDialectOptions(t);
    default: return [];
  }
};

// Check if a language supports dialect selection
const supportsDialect = (langCode) => ['es', 'fr', 'ar'].includes(langCode);

// Language names for display - localized per UI language
// Uses i18n keys from locale files for proper localization
const getLanguageNames = (t) => ({
  en: { 
    name: t('favorites.languages.en', 'English'), 
    flag: "🇺🇸", 
    placeholder: t('conversation.inputPlaceholder', 'Type your message...') 
  },
  es: { 
    name: t('favorites.languages.es', 'Español'), 
    flag: "🇪🇸", 
    placeholder: t('conversation.inputPlaceholderES', 'Escribe tu mensaje...') 
  },
  fr: { 
    name: t('favorites.languages.fr', 'Français'), 
    flag: "🇫🇷", 
    placeholder: t('conversation.inputPlaceholderFR', 'Tapez votre message...') 
  },
  ar: { 
    name: t('favorites.languages.ar', 'العربية'), 
    flag: "🇸🇦", 
    placeholder: t('conversation.inputPlaceholderAR', '...اكتب رسالتك') 
  },
  zh: { 
    name: t('favorites.languages.zh', '中文'), 
    flag: "🇨🇳", 
    placeholder: t('conversation.inputPlaceholder', '输入您的消息...') 
  },
  ja: { 
    name: t('favorites.languages.ja', '日本語'), 
    flag: "🇯🇵", 
    placeholder: t('conversation.inputPlaceholder', 'メッセージを入力...') 
  },
  ko: { 
    name: t('favorites.languages.ko', '한국어'), 
    flag: "🇰🇷", 
    placeholder: t('conversation.inputPlaceholder', '메시지를 입력하세요...') 
  },
  vi: { 
    name: t('favorites.languages.vi', 'Tiếng Việt'), 
    flag: "🇻🇳", 
    placeholder: t('conversation.inputPlaceholder', 'Nhập tin nhắn của bạn...') 
  },
  th: { 
    name: t('favorites.languages.th', 'ภาษาไทย'), 
    flag: "🇹🇭", 
    placeholder: t('conversation.inputPlaceholder', 'พิมพ์ข้อความของคุณ...') 
  }
});

const EverydayTranslatePage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  // Get localized language names based on current UI language
  const languageNames = getLanguageNames(t);
  
  const [text, setText] = useState("");
  const [translated, setTranslated] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAudioTest, setShowAudioTest] = useState(false);
  
  // Ad manager hook - CRITICAL: isPremiumUser flag prevents ads for VIP/Admin/Tester
  const { showAd, recordTranslation, dismissAd, isPremiumUser } = useAdManager();
  
  // User status hook - ADDITIONAL GUARD for VIP check
  const { hasFullAccess } = useUserStatus();
  
  // Audio manager hooks
  const {
    ttsAvailable,
    voicesLoaded,
    isSpeaking,
    selectedVoiceInfo,
    availableVoices,
    speak,
    stopSpeaking,
    isLanguageSupported,
    getVoiceInfo,
    runSelfTest
  } = useAudioManager();
  
  const {
    sttAvailable,
    micPermission,
    isListening,
    startListening,
    stopListening,
    getMicStatusMessage
  } = useMicrophoneManager();
  
  // Get user's primary language from localStorage
  const primaryLanguage = localStorage.getItem('passportTalk_primaryLanguage') || 'en';
  
  // All supported languages for translation (including Asian languages)
  const allLanguages = ['en', 'es', 'fr', 'ar', 'zh', 'ja', 'ko', 'vi', 'th'];
  
  // Source language - defaults to user's primary language
  const [sourceLang, setSourceLang] = useState(() => {
    const savedSource = localStorage.getItem('passportTalk_sourceLang');
    if (savedSource && allLanguages.includes(savedSource)) return savedSource;
    return primaryLanguage;
  });
  
  // Target language - defaults to a different language than source
  const [targetLang, setTargetLang] = useState(() => {
    const savedTarget = localStorage.getItem('passportTalk_targetLang');
    if (savedTarget && allLanguages.includes(savedTarget)) return savedTarget;
    
    // Smart default: pick a logical target based on primary language
    switch (primaryLanguage) {
      case 'en': return 'es';  // English → Spanish
      case 'es': return 'en';  // Spanish → English  
      case 'fr': return 'en';  // French → English
      case 'ar': return 'en';  // Arabic → English
      case 'zh': return 'en';  // Chinese → English
      case 'ja': return 'en';  // Japanese → English
      case 'ko': return 'en';  // Korean → English
      case 'vi': return 'en';  // Vietnamese → English
      case 'th': return 'en';  // Thai → English
      default: return 'en';
    }
  });
  
  // Settings with localStorage persistence
  const [targetGender, setTargetGender] = useState(() => {
    return localStorage.getItem('passportTalk_targetGender') || 'neutral';
  });
  
  const [dialect, setDialect] = useState(() => {
    return localStorage.getItem('passportTalk_dialect') || 'NEUTRAL';
  });

  const [dialectIntensity, setDialectIntensity] = useState(() => {
    return localStorage.getItem('passportTalk_dialectIntensity') || 'standard';
  });

  const [autoTranslate, setAutoTranslate] = useState(() => {
    const saved = localStorage.getItem('passportTalk_autoTranslate');
    return saved !== null ? saved === 'true' : true; // Default ON
  });

  // Refs for instant translation
  const abortControllerRef = useRef(null);
  const lastTranslatedTextRef = useRef("");
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);

  // Clear all state - reset input, output, cancel in-flight requests
  const handleClear = useCallback(() => {
    // Cancel any in-flight translation
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    
    // Stop TTS if playing
    if (isSpeaking) {
      stopSpeaking();
    }
    
    // Stop STT if listening
    if (isListening) {
      stopListening();
    }
    
    // Reset all state
    setText("");
    setTranslated("");
    setCopied(false);
    setLoading(false);
    lastTranslatedTextRef.current = "";
    
    // Focus input for next message
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [isSpeaking, stopSpeaking, isListening, stopListening]);

  // Persist settings to localStorage (all users can swap now)
  useEffect(() => {
    localStorage.setItem('passportTalk_sourceLang', sourceLang);
  }, [sourceLang]);

  useEffect(() => {
    localStorage.setItem('passportTalk_targetLang', targetLang);
  }, [targetLang]);

  useEffect(() => {
    localStorage.setItem('passportTalk_targetGender', targetGender);
  }, [targetGender]);

  useEffect(() => {
    localStorage.setItem('passportTalk_dialect', dialect);
  }, [dialect]);

  useEffect(() => {
    localStorage.setItem('passportTalk_dialectIntensity', dialectIntensity);
  }, [dialectIntensity]);

  useEffect(() => {
    localStorage.setItem('passportTalk_autoTranslate', String(autoTranslate));
  }, [autoTranslate]);

  // Swap source and target languages (available for all users)
  const handleSwapLanguages = () => {
    const temp = sourceLang;
    setSourceLang(targetLang);
    setTargetLang(temp);
    // Clear current translation when swapping
    setTranslated("");
    lastTranslatedTextRef.current = "";
    // Clear input text to reset the flow
    setText("");
    // Reset dialect to neutral when switching target language
    setDialect('NEUTRAL');
  };

  // Handle source language change
  const handleSourceChange = (newSource) => {
    if (newSource === targetLang) {
      // If selecting same as target, swap them
      setTargetLang(sourceLang);
    }
    setSourceLang(newSource);
    // Clear translation when changing languages
    setTranslated("");
    lastTranslatedTextRef.current = "";
  };

  // Handle target language change  
  const handleTargetChange = (newTarget) => {
    if (newTarget === sourceLang) {
      // If selecting same as source, swap them
      setSourceLang(targetLang);
    }
    setTargetLang(newTarget);
    // Reset dialect when changing target language
    setDialect('NEUTRAL');
    // Clear translation
    setTranslated("");
    lastTranslatedTextRef.current = "";
  };

  // Determine if we should show dialect/gender controls
  // Show for Spanish, French, and Arabic targets
  const showDialectControls = supportsDialect(targetLang);
  const dialectOptions = getDialectOptions(targetLang, t);

  // Instant translation with debounce - OPTIMIZED: 500ms for faster desktop feel
  const performTranslation = useCallback(async (textToTranslate, signal) => {
    try {
      const requestBody = {
        text: textToTranslate,
        source_lang: sourceLang,
        target_lang: targetLang,
        mode: "conversation"
      };
      
      // Add dialect/gender params for languages that support them
      if (supportsDialect(targetLang)) {
        requestBody.target_person_gender = targetGender;
        requestBody.dialect_country = dialect;
        requestBody.dialect_intensity = dialectIntensity;
      }
      
      const response = await api.post('/translate', requestBody, { signal });
      
      return response.data.translated_text;
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        // Request was cancelled, ignore
        return null;
      }
      throw error;
    }
  }, [sourceLang, targetLang, targetGender, dialect, dialectIntensity]);

  // Auto-translate effect with debounce - OPTIMIZED: 500ms debounce
  useEffect(() => {
    if (!autoTranslate) return;
    
    const trimmedText = text.trim();
    
    // Only trigger if text length >= 4 and changed since last translation
    if (trimmedText.length < 4 || trimmedText === lastTranslatedTextRef.current) {
      return;
    }

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Set up new debounce timer (500ms - optimized for desktop)
    debounceTimerRef.current = setTimeout(async () => {
      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();
      
      setLoading(true);
      try {
        const result = await performTranslation(trimmedText, abortControllerRef.current.signal);
        if (result) {
          setTranslated(result);
          setCopied(false);
          lastTranslatedTextRef.current = trimmedText;
          // Record successful translation for ad system
          recordTranslation();
        }
      } catch (error) {
        console.error("Auto-translate error:", error);
        // Don't show toast for auto-translate errors to avoid spam
      } finally {
        setLoading(false);
      }
    }, 500); // Reduced from 800ms to 500ms

    // Cleanup
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [text, autoTranslate, performTranslation, recordTranslation]);

  // Manual translate (always works, even with auto-translate on)
  const handleTranslate = async () => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      toast.error("Please enter text to translate");
      return;
    }

    // Cancel any pending auto-translate
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setLoading(true);
    try {
      const result = await performTranslation(trimmedText, null);
      if (result) {
        setTranslated(result);
        setCopied(false);
        lastTranslatedTextRef.current = trimmedText;
        toast.success("Translation complete!");
        // Record successful translation for ad system
        recordTranslation();
      }
    } catch (error) {
      console.error("Translation error:", error);
      toast.error(error.response?.data?.detail || error.message || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  // Smart clipboard copy with multiple fallbacks
  const copyTextSmart = async (textToCopy, inputSelector) => {
    // 1) Try modern clipboard
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(textToCopy);
        return { ok: true, method: "clipboard" };
      }
    } catch (e) {
      // Clipboard API failed, trying fallback method
      console.debug('Clipboard API unavailable, using fallback:', e.message);
    }

    // 2) Try execCommand fallback
    try {
      const ta = document.createElement("textarea");
      ta.value = textToCopy;
      ta.style.position = "fixed";
      ta.style.top = "-1000px";
      ta.style.left = "-1000px";
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      if (ok) return { ok: true, method: "execCommand" };
    } catch (e) {
      // execCommand failed, trying manual selection
      console.debug('execCommand copy failed:', e.message);
    }

    // 3) Last resort: highlight the translated text so user can Ctrl+C
    if (inputSelector) {
      const el = document.querySelector(inputSelector);
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        return { ok: false, method: "selected" };
      }
    }

    return { ok: false, method: "failed" };
  };

  const handleCopy = async () => {
    if (!translated) {
      toast.error("Nothing to copy");
      return;
    }
    
    const result = await copyTextSmart(translated, '[data-testid="translated-text"]');
    
    if (result.ok) {
      setCopied(true);
      toast.success("Copied! Paste into WhatsApp ✅");
      setTimeout(() => setCopied(false), 2000);
    } else if (result.method === "selected") {
      toast.info("Text selected — press Ctrl+C / Cmd+C to copy");
    } else {
      toast.error("Failed to copy. Tap 'Share to WhatsApp' instead.");
    }
  };

  const handleCopyClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    await handleCopy();
  };

  const handleOpenWhatsApp = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(translated)}`, "_blank");
  };

  const handleSaveFavorite = async () => {
    if (!text || !translated) {
      toast.error("Nothing to save");
      return;
    }

    try {
      await api.post('/favorites', {
        source_text: text,
        translated_text: translated,
        mode: "conversation",
        dialect_country: dialect,
        target_person_gender: targetGender
      });
      toast.success("Saved to favorites!");
    } catch (error) {
      toast.error("Failed to save favorite");
    }
  };

  // TTS - Play translated text
  // IMPORTANT: speak() called directly in click handler for browser compatibility
  const handleSpeak = () => {
    if (isSpeaking) {
      stopSpeaking();
      return;
    }
    speak(translated, targetLang);
  };

  // STT - Voice input
  const handleVoiceInput = () => {
    if (isListening) {
      stopListening();
      return;
    }
    
    startListening(
      sourceLang,
      (transcript) => {
        setText(transcript);
      },
      (error) => {
        console.error('Voice input error:', error);
      }
    );
  };

  // Audio self-test
  const handleAudioTest = () => {
    runSelfTest(targetLang);
  };

  // Get audio button state
  const getAudioButtonState = () => {
    if (!ttsAvailable) {
      return { disabled: true, reason: 'TTS not supported' };
    }
    if (!voicesLoaded) {
      return { disabled: true, reason: 'Loading voices...' };
    }
    const voiceInfo = getVoiceInfo(targetLang);
    if (!voiceInfo?.available) {
      return { disabled: true, reason: voiceInfo?.unavailableMessage || 'Voice not available' };
    }
    return { disabled: false, reason: null };
  };

  const audioButtonState = getAudioButtonState();
  const micStatusMessage = getMicStatusMessage();

  return (
    <div className={`min-h-screen pb-24 p-6 bg-gradient-to-b from-slate-50 to-white ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
      {/* Beta Ad Modal - HARD BLOCK: NEVER show to VIP/Admin/Tester */}
      {showAd && !isPremiumUser && !hasFullAccess && <BetaAdCard onDismiss={dismissAd} />}
      
      <div className="max-w-2xl mx-auto">
        {/* Header badge */}
        <div className="mb-4 flex items-center gap-2">
          <div className="bg-gradient-to-r from-[#10b981] to-[#059669] text-white px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
            <MessageCircle className="w-4 h-4" />
            {t('conversation.title')}
          </div>
        </div>

        <h1 className="text-3xl font-bold text-travel-navy font-outfit mb-2" data-testid="conversation-page-title">
          {t('nav.translate')}
        </h1>
        
        {/* Language Selector - Full interchangeability */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          {/* Source Language Dropdown */}
          <div className="relative">
            <select
              value={sourceLang}
              onChange={(e) => handleSourceChange(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="source-language-select"
            >
              {allLanguages.map(lang => (
                <option key={lang} value={lang}>
                  {languageNames[lang]?.flag} {languageNames[lang]?.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
          
          {/* Swap button */}
          <button
            onClick={handleSwapLanguages}
            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            title={t('conversation.swap')}
            aria-label={t('conversation.swap')}
            data-testid="swap-languages-btn"
          >
            <ArrowLeftRight className="w-4 h-4 text-slate-600" />
          </button>
          
          {/* Target Language Dropdown */}
          <div className="relative">
            <select
              value={targetLang}
              onChange={(e) => handleTargetChange(e.target.value)}
              className="appearance-none bg-white border border-slate-200 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-slate-700 cursor-pointer hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="target-language-select"
            >
              {allLanguages.map(lang => (
                <option key={lang} value={lang}>
                  {languageNames[lang]?.flag} {languageNames[lang]?.name}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Auto-translate toggle */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-slate-700">{t('conversation.instantToggle')}</span>
              <p className="text-xs text-slate-500">{t('conversation.instantHint')}</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={autoTranslate}
                onChange={(e) => setAutoTranslate(e.target.checked)}
                className="sr-only peer"
                data-testid="auto-translate-toggle"
              />
              <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-500"></div>
            </label>
          </div>
        </div>

        {/* Gender selector - Show for Spanish, French, and Arabic targets */}
        {showDialectControls && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">{t('conversation.speakingTo')}:</span>
              <div className="flex rounded-full bg-slate-100 p-1" data-testid="gender-selector">
                {[
                  { value: 'woman', labelKey: 'conversation.genderWoman', icon: '👩' },
                  { value: 'man', labelKey: 'conversation.genderMan', icon: '👨' },
                  { value: 'neutral', labelKey: 'conversation.genderNeutral', icon: '👤' }
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTargetGender(option.value)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-1.5 ${
                      targetGender === option.value
                        ? 'bg-white text-travel-navy shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`gender-${option.value}`}
                  >
                    <span>{option.icon}</span>
                    <span className="hidden sm:inline">{t(option.labelKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Dialect selector - Show for Spanish, French, and Arabic targets */}
        {showDialectControls && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-4 mb-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-semibold text-slate-700">{t('conversation.dialect')}:</span>
              <select
                value={dialect}
                onChange={(e) => setDialect(e.target.value)}
                className="bg-slate-100 border-0 rounded-full px-4 py-2 text-sm font-medium text-travel-navy focus:ring-2 focus:ring-[#10b981]/20 cursor-pointer"
                data-testid="dialect-selector"
              >
                {dialectOptions.map((option) => (
                  <option key={option.code} value={option.code}>
                    {option.flag} {option.label}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Dialect Intensity */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-slate-700">{t('conversation.dialectIntensity')}:</span>
              <div className="flex rounded-full bg-slate-100 p-1" data-testid="intensity-selector">
                {[
                  { code: 'standard', labelKey: 'conversation.intensityStandard' },
                  { code: 'native', labelKey: 'conversation.intensityNative' }
                ].map((option) => (
                  <button
                    key={option.code}
                    type="button"
                    onClick={() => setDialectIntensity(option.code)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      dialectIntensity === option.code
                        ? 'bg-white text-travel-navy shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`intensity-${option.code}`}
                  >
                    {t(option.labelKey)}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Mic permission banner */}
        {micPermission === 'denied' && (
          <div className="bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-4 flex items-start gap-3" data-testid="mic-denied-banner">
            <MicOff className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-800">{t('mic.micDenied')}</p>
              <p className="text-xs text-amber-700 mt-1">
                {t('mic.micDeniedDesc')}
              </p>
            </div>
          </div>
        )}

        {/* Text input */}
        <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-4">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-semibold text-slate-700">{languageNames[sourceLang]?.name}</label>
            <div className="flex items-center gap-2">
              {loading && (
                <div className="flex items-center gap-2 text-emerald-600 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('common.translating')}</span>
                </div>
              )}
              {/* Clear button - show when there's text or translation */}
              {(text || translated) && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                  data-testid="clear-button"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>{t('common.clear', 'Clear')}</span>
                </button>
              )}
            </div>
          </div>
          <textarea
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={languageNames[sourceLang]?.placeholder || t('conversation.inputPlaceholder')}
            className={`w-full h-32 bg-slate-50 border-2 border-slate-200 focus:border-[#10b981] focus:ring-4 focus:ring-[#10b981]/10 rounded-xl p-4 resize-none transition-all ${sourceLang === 'ar' ? 'text-right' : 'text-left'}`}
            dir={sourceLang === 'ar' ? 'rtl' : 'ltr'}
            data-testid="translate-input"
          />
          <div className="flex gap-2 mt-4">
            <button
              type="button"
              onClick={handleVoiceInput}
              disabled={!sttAvailable || micPermission === 'denied'}
              className={`flex-1 h-12 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                isListening
                  ? "bg-red-500 text-white animate-pulse"
                  : !sttAvailable || micPermission === 'denied'
                    ? "bg-slate-100 border-2 border-slate-200 text-slate-400 cursor-not-allowed"
                    : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
              }`}
              data-testid="voice-input-button"
              title={micStatusMessage || (isListening ? t('common.listening') : t('common.speak'))}
            >
              {micPermission === 'denied' ? (
                <MicOff className="w-5 h-5" />
              ) : (
                <Mic className="w-5 h-5" />
              )}
              <span>{isListening ? t('common.listening') : t('common.speak')}</span>
            </button>
            <button
              type="button"
              onClick={handleTranslate}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#10b981] to-[#059669] hover:from-[#059669] hover:to-[#047857] text-white rounded-full h-12 font-bold shadow-lg shadow-emerald-500/30 transition-all active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="translate-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('common.translating')}
                </>
              ) : (
                <>
                  {t('nav.translate')}
                  <ArrowRight className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </div>

        {/* Translation result */}
        {translated && (
          <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-6 mb-4" data-testid="translation-result">
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-semibold text-slate-700">{languageNames[targetLang]?.name}</label>
              {/* Only show dialect badge when translating TO Spanish */}
              {showDialectControls && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2 py-1 rounded-full">
                    {dialectOptions.find(d => d.code === dialect)?.flag} {dialectOptions.find(d => d.code === dialect)?.label}
                  </span>
                  {dialectIntensity === 'native' && (
                    <span className="text-xs text-amber-600 font-medium bg-amber-50 px-2 py-1 rounded-full">
                      {t('conversation.intensityNative')}
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className={`bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4 ${targetLang === 'ar' ? 'text-right' : 'text-left'}`} dir={targetLang === 'ar' ? 'rtl' : 'ltr'}>
              <p className="text-travel-navy text-lg" data-testid="translated-text">{translated}</p>
            </div>
            
            {/* Action buttons */}
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleCopyClick}
                className={`h-12 rounded-full font-semibold transition-all flex items-center justify-center gap-2 ${
                  copied
                    ? "bg-emerald-500 text-white"
                    : "bg-emerald-100 hover:bg-emerald-200 text-emerald-700"
                }`}
                data-testid="copy-button"
              >
                {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                <span>{copied ? t('common.copied') : t('common.copy')}</span>
              </button>
              <button
                type="button"
                onClick={handleOpenWhatsApp}
                className="bg-[#25D366] hover:bg-[#20BD5A] text-white rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="whatsapp-button"
              >
                <ExternalLink className="w-5 h-5" />
                <span>{t('common.shareWhatsApp')}</span>
              </button>
              <button
                type="button"
                onClick={handleSpeak}
                disabled={audioButtonState.disabled || !translated}
                className={`rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2 ${
                  isSpeaking
                    ? "bg-blue-500 text-white animate-pulse"
                    : audioButtonState.disabled
                      ? "bg-slate-100 border-2 border-slate-200 text-slate-400 cursor-not-allowed"
                      : "bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
                data-testid="speak-button"
                title={audioButtonState.reason || (isSpeaking ? t('common.playing') : t('common.audio'))}
              >
                {audioButtonState.disabled ? (
                  <VolumeX className="w-5 h-5" />
                ) : (
                  <Volume2 className="w-5 h-5" />
                )}
                <span>{isSpeaking ? t('common.playing') : t('common.audio')}</span>
              </button>
              <button
                type="button"
                onClick={handleSaveFavorite}
                className="bg-white border-2 border-slate-200 text-slate-700 hover:bg-slate-50 rounded-full h-12 font-semibold transition-all flex items-center justify-center gap-2"
                data-testid="save-favorite-button"
              >
                <Heart className="w-5 h-5" />
                <span>{t('common.save')}</span>
              </button>
            </div>
            
            {/* Audio status/error message */}
            {audioButtonState.disabled && audioButtonState.reason && (
              <div className="flex items-center gap-2 text-xs text-amber-600 mt-3 bg-amber-50 p-2 rounded-lg">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>{audioButtonState.reason}</span>
              </div>
            )}
            
            {/* Voice info when speaking */}
            {isSpeaking && selectedVoiceInfo && (
              <div className="flex items-center gap-2 text-xs text-blue-600 mt-3 bg-blue-50 p-2 rounded-lg">
                <Volume2 className="w-4 h-4 flex-shrink-0 animate-pulse" />
                <span>
                  {t('audio.playingWith', { voiceName: selectedVoiceInfo.name })} ({selectedVoiceInfo.lang})
                  {selectedVoiceInfo.isFallback && ` ${t('audio.fallback')}`}
                </span>
              </div>
            )}
            
            {/* Audio Self-Test */}
            <div className="mt-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowAudioTest(!showAudioTest)}
                className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                data-testid="audio-test-toggle"
              >
                <Settings2 className="w-3 h-3" />
                {t('audio.troubleshooting')}
              </button>
              
              {showAudioTest && (
                <div className="mt-2 p-3 bg-slate-50 rounded-lg space-y-2">
                  <p className="text-xs text-slate-600 font-medium">{t('audio.selfTest')}</p>
                  <button
                    type="button"
                    onClick={handleAudioTest}
                    disabled={audioButtonState.disabled}
                    className={`w-full h-10 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2 ${
                      audioButtonState.disabled
                        ? "bg-slate-200 text-slate-400 cursor-not-allowed"
                        : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                    }`}
                    data-testid="audio-self-test-btn"
                  >
                    <TestTube2 className="w-4 h-4" />
                    {t('audio.testAudio', { language: languageNames[targetLang]?.name })}
                  </button>
                  {voicesLoaded && (
                    <div className="text-xs text-slate-500 space-y-1">
                      <p>{t('audio.voiceLabel')}: {targetLang.toUpperCase()}</p>
                      {getVoiceInfo(targetLang)?.available ? (
                        <p className="text-emerald-600">
                          ✓ {getVoiceInfo(targetLang)?.voiceName} ({getVoiceInfo(targetLang)?.voiceLang})
                          {getVoiceInfo(targetLang)?.isFallback && ` [${t('audio.fallback')}]`}
                        </p>
                      ) : (
                        <p className="text-red-500">✗ {getVoiceInfo(targetLang)?.unavailableMessage}</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* WhatsApp tip */}
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-2xl p-4 text-center">
          <p className="text-sm text-emerald-700 font-medium">
            💡 {t('conversation.tip')}
          </p>
        </div>
      </div>

      <BottomTabNav />
    </div>
  );
};

export default EverydayTranslatePage;
